import { useState } from "react";
import { Button, DataTable, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { listSyncConflicts, resolveSyncConflict } from "../../api/syncApi";
import type { SyncConflictOut } from "../../api/types";
import { ApiError } from "../../lib/apiClient";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";

/**
 * §3.5/§9.5's "Needs Review" reconciliation queue. **Now has a real resolve/dismiss
 * action** (known gap fixed §10.5): `GET /sync/conflicts` (real ids, unlike the old
 * `GET /sync/downstream`-derived view) + `PATCH /sync/conflicts/{id}`. Resolving here
 * only marks the CONFLICT RECORD resolved/dismissed — it never retroactively touches
 * the underlying inventory/sale data; fixing that (e.g. a manual stock adjustment) is
 * still a separate action, exactly as before.
 */
export function ConflictsPage() {
  const { ensureFreshToken } = useAuth();
  const [showResolved, setShowResolved] = useState(false);
  const conflictsState = useAsyncData(
    () => ensureFreshToken().then((token) => listSyncConflicts(token, showResolved ? undefined : false)),
    [showResolved],
  );
  const [actingOn, setActingOn] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState<Record<string, string>>({});
  const [actionError, setActionError] = useState<string | null>(null);

  async function handleAction(conflict: SyncConflictOut, status: "RESOLVED" | "DISMISSED") {
    setActionError(null);
    setActingOn(conflict.id);
    try {
      const token = await ensureFreshToken();
      await resolveSyncConflict(token, conflict.id, { status, resolution_note: noteDraft[conflict.id] || undefined });
      conflictsState.reload();
    } catch (error) {
      setActionError(error instanceof ApiError ? error.message : "Could not update this conflict.");
    } finally {
      setActingOn(null);
    }
  }

  const columns: DataTableColumn<SyncConflictOut, keyof SyncConflictOut>[] = [
    { key: "entity_type", header: "Entity", render: (r) => r.entity_type },
    { key: "entity_id", header: "Entity ID", render: (r) => <span className="muted">{r.entity_id.slice(0, 8)}</span> },
    { key: "status", header: "Status", render: (r) => r.status },
    { key: "reason", header: "Reason", render: (r) => r.reason ?? "—" },
    { key: "created_at", header: "Created", render: (r) => new Date(r.created_at).toLocaleString() },
    {
      key: "resolved",
      header: "Resolved?",
      render: (r) =>
        r.resolved ? (
          <span className="badge badge--current">Resolved{r.resolved_at ? ` ${new Date(r.resolved_at).toLocaleDateString()}` : ""}</span>
        ) : (
          <span className="badge badge--overdue">No</span>
        ),
    },
    {
      key: "id",
      header: "Action",
      render: (r) =>
        r.resolved ? (
          <span className="muted">—</span>
        ) : (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            <input
              className="sl-input"
              style={{ width: 140 }}
              placeholder="note (optional)"
              value={noteDraft[r.id] ?? ""}
              onChange={(e) => setNoteDraft((prev) => ({ ...prev, [r.id]: e.target.value }))}
            />
            <Button
              variant="secondary"
              size="md"
              type="button"
              disabled={actingOn === r.id}
              onClick={() => void handleAction(r, "RESOLVED")}
            >
              Resolve
            </Button>
            <Button variant="ghost" size="md" type="button" disabled={actingOn === r.id} onClick={() => void handleAction(r, "DISMISSED")}>
              Dismiss
            </Button>
          </div>
        ),
    },
  ];

  const unresolved = (conflictsState.data ?? []).filter((c) => !c.resolved);

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Needs Review</h1>
          <p className="page__subtitle">Sync conflicts awaiting reconciliation.</p>
        </div>
        <Button variant="secondary" onClick={conflictsState.reload}>
          Refresh
        </Button>
      </div>

      {conflictsState.error ? <StatusBanner kind="error">{conflictsState.error}</StatusBanner> : null}
      {actionError ? <StatusBanner kind="error">{actionError}</StatusBanner> : null}

      <StatusBanner kind="info">
        Resolving/dismissing here only marks the conflict record itself — it never retroactively changes the
        underlying inventory/sale data. Investigate and fix the underlying entity separately (e.g. a manual stock
        adjustment or journal entry) before marking a conflict resolved.
      </StatusBanner>

      <div className="stat-row">
        <div className="stat-tile">
          <p className="stat-tile__label">Unresolved</p>
          <p className="stat-tile__value stat-tile__value--negative">{unresolved.length}</p>
        </div>
        <div className="stat-tile">
          <p className="stat-tile__label">Shown</p>
          <p className="stat-tile__value">{(conflictsState.data ?? []).length}</p>
        </div>
      </div>

      <label className="sl-field" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} />
        <span className="sl-field__label" style={{ margin: 0 }}>
          Include already-resolved/dismissed
        </span>
      </label>

      <div className="panel">
        {conflictsState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable
            columns={columns}
            rows={conflictsState.data ?? []}
            getRowId={(row) => row.id}
            emptyMessage="No conflicts recorded."
            caption="Needs Review"
          />
        )}
      </div>
    </div>
  );
}
